import Box from '@mui/material/Box';

import { NotificationCard } from './notification-card';
import { useStackLift } from './use-stack-lift';
import type { BannerItem } from './types';

const MAX_VISIBLE = 3;
const DESKTOP_WIDTH = 360;
const SAFE_AREA_BOTTOM = 'env(safe-area-inset-bottom, 0px)';

export interface NotificationBannerProps {
  /** Newest first. The first three show, and the rest wait for a place. */
  items: BannerItem[];
  onDismiss: (id: string) => void;
  /** The accessible name of the close button, translated by the app. */
  dismissLabel: string;
}

/**
 * Floating server notifications (CLOUD-1289). They sit over the page and never move it.
 * They stack above any control registered with `useNotificationAvoid`.
 * Bottom-right on desktop, full width at the bottom on a phone. Not a toast: they stay until dismissed or ended.
 */
export function NotificationBanner({ items, onDismiss, dismissLabel }: NotificationBannerProps) {
  const stackRef = useStackLift();
  const visible = items.slice(0, MAX_VISIBLE);

  if (visible.length === 0) return null;

  return (
    <Box
      ref={stackRef}
      data-testid="notification-banner"
      sx={(theme) => {
        const base = `calc(${SAFE_AREA_BOTTOM} + ${theme.spacing(2)})`;
        const lifted = `calc(var(--notification-lift, 0px) + ${theme.spacing(1)})`;
        const bottom = `max(${base}, ${lifted})`;
        return {
          position: 'fixed',
          zIndex: theme.zIndex.snackbar,
          left: theme.spacing(2),
          right: theme.spacing(2),
          bottom,
          maxHeight: `calc(100dvh - ${bottom} - ${theme.spacing(2)})`,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: 1,
          pointerEvents: 'none',
          [theme.breakpoints.up('sm')]: { left: 'auto', width: DESKTOP_WIDTH },
        };
      }}
    >
      {visible.map((item) => (
        <NotificationCard key={item.id} item={item} dismissLabel={dismissLabel} onDismiss={onDismiss} />
      ))}
    </Box>
  );
}
